import {
  type GeoJSONSource,
  type MapGeoJSONFeature,
  Map as MapLibreMap,
  Marker,
  NavigationControl,
  Popup,
} from 'maplibre-gl';
import { pickLocation } from './location-picker.ts';
import { useOwnWorker, zoomHelp } from './maplibre-worker.ts';

// Centre of Germany, for when the event location is not configured yet.
const GERMANY: [number, number] = [10.45, 51.16];
const OPEN = '#2563eb';
const FULL = '#78716c';

interface RideProperties {
  place: string;
  placeId: number;
  /** "85 km, about 1 h 10 min to the event" */
  route?: string;
  when: string;
  seats: string;
  full: boolean;
  driver?: string;
  note?: string;
  request?: { text: string; url?: string };
}

const container = document.getElementById('map');
if (container) {
  useOwnWorker();

  const data = container.dataset;
  const event: [number, number] | undefined =
    data.lat && data.lon ? [Number(data.lon), Number(data.lat)] : undefined;
  // After a search for rides near a place: that place, and the towns whose rides start or pass near.
  const near: [number, number] | undefined =
    data.nearLat && data.nearLon ? [Number(data.nearLon), Number(data.nearLat)] : undefined;
  const nearPlaces = (data.nearPlaces ?? '').split(',').filter(Boolean).map(Number);

  const map = new MapLibreMap({
    container,
    style: data.style ?? '',
    center: near ?? event ?? GERMANY,
    zoom: near ? 8 : event ? 6 : 5,
    // The map sits inside a scrolling page (and on the website in an iframe): scrolling the page
    // must not zoom the map.
    cooperativeGestures: true,
    locale: zoomHelp(data),
  });
  map.addControl(new NavigationControl({ showCompass: false }));

  if (event) {
    new Marker({ color: '#c2410c' })
      .setLngLat(event)
      .setPopup(new Popup({ offset: 25 }).setText(data.name ?? ''))
      .addTo(map);
  }
  if (near) {
    new Marker({ color: '#15803d', scale: 0.8 })
      .setLngLat(near)
      .setPopup(new Popup({ offset: 20 }).setText(data.nearName ?? ''))
      .addTo(map);
  }

  map.on('load', async () => {
    if (!data.event) return;
    const eventId = encodeURIComponent(data.event);
    const [ridesRes, routesRes] = await Promise.all([
      fetch(`/api/rides?lang=${encodeURIComponent(data.locale ?? '')}&event=${eventId}`),
      fetch(`/api/routes?event=${eventId}`),
    ]);
    if (!ridesRes.ok) return;
    const rides = await ridesRes.json();
    const ridesByPlace = new Map<number, RideProperties[]>();
    for (const { properties } of rides.features as { properties: RideProperties }[]) {
      ridesByPlace.set(properties.placeId, [...(ridesByPlace.get(properties.placeId) ?? []), properties]);
    }

    // Below the pins: the way from each town to the event. Many routes share the last part, and a
    // click there lists every ride that passes.
    if (routesRes.ok) {
      map.addSource('routes', {
        type: 'geojson',
        data: await routesRes.json(),
        attribution:
          '<a href="https://openrouteservice.org/">openrouteservice.org</a> by HeiGIT, <a href="https://project-osrm.org/">OSRM</a>',
      });
      map.addLayer({
        id: 'routes',
        type: 'line',
        source: 'routes',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          'line-sort-key': ['case', ['get', 'full'], 0, 1],
        },
        paint: {
          'line-color': ['case', ['get', 'full'], FULL, OPEN],
          'line-opacity': ['case', ['get', 'full'], 0.4, 0.6],
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 2.5, 12, 5],
        },
      });
      map.addLayer({
        id: 'route-highlight',
        type: 'line',
        source: 'routes',
        filter: ['in', ['get', 'placeId'], ['literal', nearPlaces]],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['case', ['get', 'full'], FULL, OPEN],
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 5, 12, 8],
        },
      });
      // Invisible and wide: the lines are thin to hit with a finger or the mouse.
      map.addLayer({
        id: 'route-target',
        type: 'line',
        source: 'routes',
        paint: { 'line-color': '#000000', 'line-opacity': 0, 'line-width': 16 },
      });
    }

    // Rides from the same town lie on the same point; clusters keep them apart until zoomed in, and
    // at the last zoom level the popup lists all of them.
    map.addSource('rides', { type: 'geojson', data: rides, cluster: true, clusterMaxZoom: 11 });
    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'rides',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': OPEN,
        'circle-radius': ['step', ['get', 'point_count'], 14, 5, 18, 15, 24],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'rides',
      filter: ['has', 'point_count'],
      layout: { 'text-field': '{point_count_abbreviated}', 'text-font': ['Noto Sans Bold'], 'text-size': 13 },
      paint: { 'text-color': '#ffffff' },
    });
    map.addLayer({
      id: 'rides',
      type: 'circle',
      source: 'rides',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': ['case', ['get', 'full'], FULL, OPEN],
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });

    map.on('click', 'clusters', async (e) => {
      const feature = e.features?.[0];
      if (feature?.geometry.type !== 'Point') return;
      const source = map.getSource('rides') as GeoJSONSource;
      const zoom = await source.getClusterExpansionZoom(feature.properties.cluster_id);
      map.easeTo({ center: feature.geometry.coordinates as [number, number], zoom });
    });

    let popup: Popup | undefined;
    // Without a popup, the routes near the searched place stay highlighted.
    const highlight = (placeIds: number[]) => {
      if (!map.getLayer('route-highlight')) return;
      map.setFilter('route-highlight', ['in', ['get', 'placeId'], ['literal', placeIds]]);
    };
    function showRides(at: [number, number], features: MapGeoJSONFeature[], offset: number) {
      const placeIds = [...new Set(features.map((f) => Number(f.properties.placeId)))];
      highlight(placeIds);
      const opened = new Popup({ offset, maxWidth: '18rem' })
        .setLngLat(at)
        .setDOMContent(popupContent(placeIds.map((id) => ridesByPlace.get(id) ?? [])))
        .addTo(map);
      // The previous popup closes after this one opened: only the current one clears the highlight.
      opened.on('close', () => {
        if (popup !== opened) return;
        popup = undefined;
        highlight(nearPlaces);
      });
      popup = opened;
    }

    map.on('click', 'rides', (e) => {
      const first = e.features?.[0];
      if (first?.geometry.type !== 'Point') return;
      showRides(first.geometry.coordinates as [number, number], e.features ?? [], 12);
    });

    map.on('click', 'route-target', (e) => {
      // A pin on top of the line opens its own popup.
      if (map.queryRenderedFeatures(e.point, { layers: ['rides', 'clusters'] }).length) return;
      showRides([e.lngLat.lng, e.lngLat.lat], e.features ?? [], 0);
    });

    for (const layer of ['clusters', 'rides', 'route-target']) {
      map.on('mouseenter', layer, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layer, () => {
        map.getCanvas().style.cursor = '';
      });
    }
  });
}

const picker = document.getElementById('location-map');
if (picker) pickLocation(picker);

function requestElement(request: { text: string; url?: string }) {
  const el = document.createElement(request.url ? 'a' : 'div');
  el.textContent = request.text;
  el.className = 'request';
  if (request.url && el instanceof HTMLAnchorElement) el.href = request.url;
  return el;
}

/** Per town its route and rides. Built with textContent: the texts come from people's input. */
function popupContent(places: RideProperties[][]) {
  const root = document.createElement('div');
  root.className = 'ride-popup';
  const add = (parent: HTMLElement, tag: string, text: string | undefined, className?: string) => {
    if (!text) return;
    const el = document.createElement(tag);
    el.textContent = text;
    if (className) el.className = className;
    parent.append(el);
  };
  for (const rides of places) {
    const [first] = rides;
    if (!first) continue;
    const place = document.createElement('section');
    add(place, 'strong', first.place);
    add(place, 'div', first.route, 'route');
    for (const ride of rides) {
      const item = document.createElement('div');
      item.className = 'ride';
      add(item, 'div', ride.when);
      add(item, 'div', ride.seats, ride.full ? 'full' : 'open');
      add(item, 'div', ride.driver);
      add(item, 'div', ride.note, 'note');
      if (ride.request) item.append(requestElement(ride.request));
      place.append(item);
    }
    root.append(place);
  }
  return root;
}
